import Loading, { LoadingBall } from '@ingka/loading';
import Text from '@ingka/text';
import { isDeleteLotteryAllowed, isEditLotteryAllowed } from '@lotta/feature-lottery-rules';
import {
  useGetLotteryQuery,
  useGetRetailUnitQuery,
} from '@lotta/self-service/lottery-fe/data-access-state';
import {
  useAuthExpiration,
  useErrorToast,
  useLocale,
  useNavigateWithLocale,
} from '@lotta/self-service/lottery-fe/feature-hooks';
import { EditLotteryForm } from '@lotta/self-service/lottery-fe/feature-lottery-form';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styles from './EditLottery.module.scss';

/* eslint-disable-next-line */
export interface EditLotteryProps {}

export const EditLottery: React.FC<EditLotteryProps> = () => {
  const isTokenExpired = useAuthExpiration();
  const { lotteryId } = useParams();
  const { retailUnit } = useLocale();
  const navigate = useNavigateWithLocale();
  const { t } = useTranslation();

  const {
    data: retailUnitData,
    isLoading: retailUnitIsLoading,
    error: retailUnitError,
  } = useGetRetailUnitQuery({ retailUnit });
  useErrorToast(retailUnitError);

  const { data, isLoading, error } = useGetLotteryQuery(
    {
      retailUnit,
      lotteryId: lotteryId as string,
    },
    { skip: !lotteryId }
  );
  useErrorToast(error, { 404: 'edit-lottery-getting-error-msg' });

  const onFinishedEditing = useCallback(
    () => navigate(`/lottery/${lotteryId}`),
    [lotteryId, navigate]
  );

  if (isTokenExpired) {
    return <Text headingSize="m">{t('lottery-form-auth-expired-text')}</Text>;
  }

  if (retailUnitIsLoading || isLoading) {
    return (
      <Loading text={`${t('general-fetching-lottery')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  if (error || !data || !lotteryId || !retailUnitData) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }

  const { lottery } = data;

  if (!isEditLotteryAllowed(lottery, retailUnitData.retailUnit.timeZone, new Date())) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }

  const isDeleteEnabled = isDeleteLotteryAllowed(
    lottery,
    retailUnitData.retailUnit.timeZone,
    new Date()
  );

  // OPEN before first signup start (same as when delete is enabled),
  // LIMITED afterwards
  const editMode = isDeleteEnabled ? 'OPEN' : 'LIMITED';

  return (
    <div className={styles['container-div']}>
      <h1 className={styles['header']}>{t('edit-lottery-header')}</h1>
      <div className={styles['container']}>
        <div className={styles['form-container']}>
          <EditLotteryForm
            retailUnit={retailUnit}
            lotteryId={lotteryId}
            editMode={editMode}
            onLotteryEdited={onFinishedEditing}
            onCancelled={onFinishedEditing}
          />
        </div>
      </div>
    </div>
  );
};
